"use client"

import {
  Activity,
  ArrowRight,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Gift,
  Globe2,
  HelpCircle,
  ShieldCheck,
  LogOut,
  Map as MapIcon,
  Settings2,
  Sparkles,
  TicketPercent,
  UserRound,
  UsersRound,
  Wallet,
  Wifi,
  type LucideIcon,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import type { MouseEvent } from "react"
import {
  localizePathWithQuery,
  localizeWebAccountPath,
} from "@/lib/account-links"
import { useAuth } from "@/modules/auth"
import type { AuthCustomer } from "@/modules/auth"
import { ActionRow } from "@/modules/account/shared/ActionRow"
import { EmptyState } from "@/modules/account/shared/EmptyState"
import { PrimaryActionCard } from "@/modules/account/shared/PrimaryActionCard"
import { StatCard } from "@/modules/account/shared/StatCard"
import enAccount from "../../../../../public/locales/en/account.json"
import zhCNAccount from "../../../../../public/locales/zh-CN/account.json"
import zhTWAccount from "../../../../../public/locales/zh-TW/account.json"
import zhHKAccount from "../../../../../public/locales/zh-HK/account.json"
import esAccount from "../../../../../public/locales/es/account.json"
import frFRAccount from "../../../../../public/locales/fr-FR/account.json"
import jaJPAccount from "../../../../../public/locales/ja-JP/account.json"
import koAccount from "../../../../../public/locales/ko/account.json"
import deDEAccount from "../../../../../public/locales/de-DE/account.json"
import itITAccount from "../../../../../public/locales/it-IT/account.json"
import nlNLAccount from "../../../../../public/locales/nl-NL/account.json"
import ptBRAccount from "../../../../../public/locales/pt-BR/account.json"
import ptPTAccount from "../../../../../public/locales/pt-PT/account.json"
import plPLAccount from "../../../../../public/locales/pl-PL/account.json"

type AccountCenterProps = {
  lng: string
}

type AccountLinks = {
  account: string
  buy: string
  profile: string
  redemption: string
  freeEsim: string
  referrals: string
  helpCenter: string
  realnameVerification: string
  travelMap: string
  esim: string
  balance: string
  coupons: string
}

type AccountMessages = Record<string, string>
type AccountResource = Record<string, unknown>

function extractAccountMessages(resource: AccountResource): AccountMessages {
  return Object.fromEntries(
    Object.entries(resource).filter(([, value]) => typeof value === "string")
  ) as AccountMessages
}

const accountMessagesByLocale = {
  en: enAccount,
  "zh-CN": zhCNAccount,
  "zh-TW": zhTWAccount,
  "zh-HK": zhHKAccount,
  es: esAccount,
  "fr-FR": frFRAccount,
  "ja-JP": jaJPAccount,
  ko: koAccount,
  "de-DE": deDEAccount,
  "it-IT": itITAccount,
  "nl-NL": nlNLAccount,
  "pt-BR": ptBRAccount,
  "pt-PT": ptPTAccount,
  "pl-PL": plPLAccount,
} satisfies Record<string, AccountResource>

const accountCenterKeys = [
  "identityGreeting",
  "heroTitle",
  "heroSubtitle",
  "valueEsimTitle",
  "valueEsimDesc",
  "valueOrderTitle",
  "valueOrderDesc",
  "valueRewardTitle",
  "valueRewardDesc",
  "identityViewProfile",
  "identityLogout",
  "sectionEsim",
  "sectionEsimEmpty",
  "sectionEsimEmptyDesc",
  "sectionEsimEmptyCta",
  "sectionPreview",
  "previewActiveEsim",
  "previewDataUsed",
  "previewDataUnit",
  "previewCountries",
  "previewCountriesUnit",
  "previewRewards",
  "previewRewardsUnit",
  "sectionStart",
  "esimTitle",
  "esimDesc",
  "esimCta",
  "loginPrimary",
  "redeemTitle",
  "redeemDesc",
  "redeemCta",
  "freeTitle",
  "freeDesc",
  "freeCta",
  "sectionMore",
  "inviteTitle",
  "inviteDesc",
  "helpTitle",
  "helpDesc",
  "realnameTitle",
  "realnameDesc",
  "realnameCta",
  "balanceTitle",
  "balanceDesc",
  "balanceCta",
  "couponsTitle",
  "couponsDesc",
  "couponsCta",
  "sectionSettings",
  "settingsToggle",
  "settingsHide",
  "settingTravelMap",
  "settingTravelMapDesc",
  "settingNotifications",
  "settingNotificationsDesc",
  "settingProfile",
  "settingProfileDesc",
  "back",
  "home",
  "close",
  "redeemSourceTitle",
  "redeemYoho",
  "redeemAmazonUs",
  "redeemAmazonJp",
  "redeemAmazonJpHost",
] as const

type AccountCenterTextKey = (typeof accountCenterKeys)[number]
type TextSet = Record<AccountCenterTextKey, string>

function getAccountMessages(lng: string): AccountMessages {
  const messages = accountMessagesByLocale[lng as keyof typeof accountMessagesByLocale]
  if (!messages) {
    // i18n missing namespace: warn + fallback to en instead of crashing the page.
    // Adding a new locale should not require a code change in this file just to avoid 500.
    if (typeof console !== "undefined") {
      console.warn(`[account-center] no account namespace for "${lng}", falling back to en`)
    }
    return extractAccountMessages(accountMessagesByLocale.en)
  }
  return extractAccountMessages(messages)
}

function getText(lng: string): TextSet {
  const messages = getAccountMessages(lng)
  return Object.fromEntries(
    accountCenterKeys.map((key) => {
      const messageKey = `center.${key}`
      const value = messages[messageKey]
      if (value === undefined) {
        throw new Error(`Missing account center translation for ${lng}: ${messageKey}`)
      }
      return [key, value]
    })
  ) as TextSet
}

function buildAccountLinks(lng: string): AccountLinks {
  return {
    account: localizeWebAccountPath("/account", lng),
    buy: localizeWebAccountPath("/buy", lng),
    profile: localizeWebAccountPath("/account/me", lng),
    redemption: localizeWebAccountPath("/account/redemption", lng),
    freeEsim: localizeWebAccountPath("/freeesim", lng),
    referrals: localizeWebAccountPath("/account/referrals", lng),
    helpCenter: localizeWebAccountPath("/help-center", lng),
    // Internal hub that lists every real-name verification we support.
    // Picking a specific carrier (CMLink today, others later) happens on
    // the hub page; the account card is intentionally provider-neutral.
    realnameVerification: localizeWebAccountPath(
      "/account/verifications",
      lng
    ),
    travelMap: localizeWebAccountPath("/travel-map", lng),
    esim: localizeWebAccountPath("/account/esim", lng),
    balance: localizeWebAccountPath("/account/balance", lng),
    coupons: localizeWebAccountPath("/account/coupons", lng),
  }
}

function getDisplayName(customer: AuthCustomer) {
  const name = [customer.first_name, customer.last_name]
    .filter(Boolean)
    .join(" ")
    .trim()
  return name || customer.email || customer.phone || customer.id
}

function getDisplaySubtext(customer: AuthCustomer) {
  return customer.email || customer.phone || customer.id
}

function getInitials(customer: AuthCustomer) {
  const firstChar = (
    customer.first_name ||
    customer.email ||
    customer.phone ||
    "?"
  )
    .trim()
    .charAt(0)
    .toUpperCase()
  return firstChar || "?"
}

export default function AccountCenter({ lng }: AccountCenterProps) {
  const auth = useAuth()
  const [isMounted, setIsMounted] = useState(false)
  const [showMoreSettings, setShowMoreSettings] = useState(false)
  const text = useMemo(() => getText(lng), [lng])
  const links = useMemo(() => buildAccountLinks(lng), [lng])

  const isAuthResolved =
    auth.status === "authenticated" || auth.status === "unauthenticated"
  const isLoggedIn = auth.status === "authenticated" && !!auth.customer

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (isMounted && isAuthResolved && isLoggedIn) {
      document.getElementById("account-ssr-fallback")?.remove()
    }
  }, [isMounted, isAuthResolved, isLoggedIn])

  useEffect(() => {
    if (showMoreSettings) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [showMoreSettings])

  if (!isMounted || !isAuthResolved) return null
  if (!isLoggedIn) return null

  const customer = auth.customer!

  if (showMoreSettings) {
    return (
      <>
        <MoreSettingsPanel
          text={text}
          links={links}
          onBack={() => setShowMoreSettings(false)}
        />
      </>
    )
  }

  return (
    <>
      <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-8 md:gap-10">
        <section className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-stretch lg:gap-10">
          <AccountHeroIntro text={text} />
          <IdentityBar
            customer={customer}
            text={text}
            profileHref={links.profile}
            onLogout={() => auth.logout({ redirectTo: links.account })}
          />
        </section>

        <AccountShortcutGrid text={text} links={links} />
      </div>
    </>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[13px] font-extrabold uppercase tracking-[0.16em] text-stone-500">
      {children}
    </h2>
  )
}

function AccountHeroIntro({ text }: { text: TextSet }) {
  return (
    <div className="flex flex-col justify-center gap-6">
      <span className="inline-flex w-fit items-center gap-2 rounded-full bg-orange-50 px-4 py-2 text-[13px] font-bold text-orange-700">
        <UserRound className="h-4 w-4" aria-hidden="true" />
        {text.identityGreeting}
      </span>
      <div className="grid gap-4">
        <h1 className="max-w-3xl text-[36px] font-bold leading-[1.08] tracking-[-0.04em] text-stone-950 md:text-[56px] lg:text-[60px]">
          {text.heroTitle}
        </h1>
        <p className="max-w-2xl text-[16px] leading-7 text-stone-600 md:text-[18px] md:leading-8">
          {text.heroSubtitle}
        </p>
      </div>
    </div>
  )
}

function IdentityBar({
  customer,
  text,
  profileHref,
  onLogout,
}: {
  customer: AuthCustomer
  text: TextSet
  profileHref: string
  onLogout: () => void
}) {
  return (
    <div className="flex min-h-[320px] rounded-3xl bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 p-5 text-white shadow-[0_24px_58px_-30px_rgba(234,88,12,0.8)] md:p-6 lg:h-full">
      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-[12px] font-bold uppercase tracking-[0.16em] text-white/80">
              {text.identityGreeting}
            </span>
            <h3 className="truncate text-[28px] font-bold leading-tight tracking-[-0.03em] text-white">
              {getDisplayName(customer)}
            </h3>
            <p className="text-white/82 truncate text-[14px] leading-6">
              {getDisplaySubtext(customer)}
            </p>
          </div>
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-[18px] font-extrabold text-white ring-1 ring-inset ring-white/35">
            {getInitials(customer)}
          </span>
        </div>

        <div className="mt-auto grid gap-3">
          <a
            href={profileHref}
            className="group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 text-[15px] font-extrabold text-orange-600 shadow-[0_14px_28px_-18px_rgba(28,25,23,0.65)] transition hover:bg-orange-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <UserRound className="h-4 w-4" aria-hidden="true" />
            {text.identityViewProfile}
            <ArrowRight
              className="h-4 w-4 transition group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </a>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-white/35 bg-white/10 px-4 text-[15px] font-bold text-white transition hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            aria-label={text.identityLogout}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {text.identityLogout}
          </button>
        </div>
      </div>
    </div>
  )
}

const shortcutToneClassNames = {
  neutral: {
    border: "hover:border-orange-200 hover:bg-orange-50/20",
    icon: "bg-white text-orange-600 ring-stone-200 group-hover:ring-orange-200",
    title: "text-stone-950",
    cta: "text-orange-600",
    arrow: "group-hover:text-orange-600",
  },
  orange: {
    border: "hover:border-orange-200 hover:bg-orange-50/20",
    icon: "bg-orange-50 text-orange-600 ring-orange-200 group-hover:bg-orange-100",
    title: "text-orange-700",
    cta: "text-orange-600",
    arrow: "group-hover:text-orange-600",
  },
  teal: {
    border: "hover:border-teal-200 hover:bg-teal-50/20",
    icon: "bg-teal-50 text-teal-600 ring-teal-200 group-hover:bg-teal-100",
    title: "text-teal-700",
    cta: "text-teal-600",
    arrow: "group-hover:text-teal-600",
  },
  purple: {
    border: "hover:border-violet-200 hover:bg-violet-50/20",
    icon: "bg-violet-50 text-violet-600 ring-violet-200 group-hover:bg-violet-100",
    title: "text-violet-700",
    cta: "text-violet-600",
    arrow: "group-hover:text-violet-600",
  },
}

function AccountShortcutCard({
  href,
  icon: Icon,
  title,
  description,
  cta,
  tone = "neutral",
  onClick,
}: {
  href: string
  icon: LucideIcon
  title: string
  description: string
  cta: string
  tone?: keyof typeof shortcutToneClassNames
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}) {
  const toneClassNames = shortcutToneClassNames[tone]

  // Off-site URLs (e.g. the CMLink real-name verification portal) open in a
  // new tab so the user doesn't lose their account session. Auto-detected by
  // protocol so callers don't have to opt in.
  const isExternal = /^https?:\/\//i.test(href)

  return (
    <a
      href={href}
      onClick={onClick}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className={`group flex min-h-[150px] flex-col justify-between rounded-3xl border border-stone-200 bg-white p-5 shadow-[0_18px_50px_-34px_rgba(28,25,23,0.45)] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 ${toneClassNames.border}`}
    >
      <span className="flex items-start justify-between gap-4">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ring-1 transition ${toneClassNames.icon}`}
        >
          <Icon className="h-[19px] w-[19px]" aria-hidden="true" />
        </span>
        <ArrowRight
          className={`h-4 w-4 text-stone-400 transition group-hover:translate-x-0.5 ${toneClassNames.arrow}`}
          aria-hidden="true"
        />
      </span>
      <span className="mt-5 grid gap-2">
        <span
          className={`text-[18px] font-extrabold leading-tight tracking-[-0.01em] ${toneClassNames.title}`}
        >
          {title}
        </span>
        <span className="text-[13.5px] leading-5 text-stone-600">
          {description}
        </span>
        <span
          className={`mt-1 text-[13px] font-extrabold ${toneClassNames.cta}`}
        >
          {cta}
        </span>
      </span>
    </a>
  )
}

function AccountShortcutGrid({
  text,
  links,
}: {
  text: TextSet
  links: AccountLinks
}) {
  return (
    <section>
      <SectionLabel>{text.sectionStart}</SectionLabel>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <AccountShortcutCard
          href={links.balance}
          icon={Wallet}
          title={text.balanceTitle}
          description={text.balanceDesc}
          cta={text.balanceCta}
          tone="orange"
        />
        <AccountShortcutCard
          href={links.redemption}
          icon={Gift}
          title={text.redeemTitle}
          description={text.redeemDesc}
          cta={text.redeemCta}
          tone="orange"
        />
        <AccountShortcutCard
          href={links.coupons}
          icon={TicketPercent}
          title={text.couponsTitle}
          description={text.couponsDesc}
          cta={text.couponsCta}
        />
        <AccountShortcutCard
          href={links.esim}
          icon={Wifi}
          title={text.esimTitle}
          description={text.esimDesc}
          cta={text.esimCta}
        />
        <AccountShortcutCard
          href={links.helpCenter}
          icon={HelpCircle}
          title={text.helpTitle}
          description={text.helpDesc}
          cta={text.loginPrimary}
        />
        <AccountShortcutCard
          href={links.realnameVerification}
          icon={ShieldCheck}
          title={text.realnameTitle}
          description={text.realnameDesc}
          cta={text.realnameCta}
        />
        <AccountShortcutCard
          href={links.freeEsim}
          icon={Sparkles}
          title={text.freeTitle}
          description={text.freeDesc}
          cta={text.freeCta}
          tone="teal"
        />
        <AccountShortcutCard
          href={links.referrals}
          icon={UsersRound}
          title={text.inviteTitle}
          description={text.inviteDesc}
          cta={text.loginPrimary}
          tone="purple"
        />
      </div>
    </section>
  )
}
function SettingsCollapse({
  text,
  links,
  onMoreSettings,
}: {
  text: TextSet
  links: AccountLinks
  onMoreSettings: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-2xl px-1 py-2 text-left transition hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
        aria-expanded={open}
      >
        <span className="text-[12px] font-semibold uppercase tracking-[0.16em] text-stone-500">
          {text.sectionSettings}
        </span>
        <span className="flex items-center gap-1 text-[12px] font-semibold text-stone-500">
          {open ? text.settingsHide : text.settingsToggle}
          <ChevronDown
            className={`h-4 w-4 transition ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </span>
      </button>
      {open ? (
        <div className="mt-3 grid gap-1 rounded-2xl border border-stone-200 bg-white p-2 shadow-[0_1px_2px_rgba(28,25,23,0.04)]">
          <ActionRow
            icon={UserRound}
            title={text.settingProfile}
            description={text.settingProfileDesc}
            href={links.profile}
          />
          <div className="h-px bg-stone-100" />
          <ActionRow
            icon={MapIcon}
            title={text.settingTravelMap}
            description={text.settingTravelMapDesc}
            href={links.travelMap}
          />
          <div className="h-px bg-stone-100" />
          <ActionRow
            icon={Bell}
            title={text.settingNotifications}
            description={text.settingNotificationsDesc}
            onClick={onMoreSettings}
          />
        </div>
      ) : null}
    </section>
  )
}

function MoreSettingsPanel({
  text,
  links,
  onBack,
}: {
  text: TextSet
  links: AccountLinks
  onBack: () => void
}) {
  return (
    <div className="flex flex-col gap-6 pt-4">
      <div className="flex items-center gap-1 text-sm font-semibold text-stone-700">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex min-h-11 items-center gap-1 rounded-xl px-2 transition hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {text.home}
        </button>
        <span aria-hidden="true">›</span>
        <span>{text.sectionSettings}</span>
      </div>

      <section>
        <SectionLabel>{text.sectionSettings}</SectionLabel>
        <div className="mt-4 grid gap-1 rounded-2xl border border-stone-200 bg-white p-2 shadow-[0_1px_2px_rgba(28,25,23,0.04)]">
          <ActionRow
            icon={UserRound}
            title={text.settingProfile}
            description={text.settingProfileDesc}
            href={links.profile}
          />
          <div className="h-px bg-stone-100" />
          <ActionRow
            icon={MapIcon}
            title={text.settingTravelMap}
            description={text.settingTravelMapDesc}
            href={links.travelMap}
          />
          <div className="h-px bg-stone-100" />
          <ActionRow
            icon={Bell}
            title={text.settingNotifications}
            description={text.settingNotificationsDesc}
          />
        </div>
      </section>
    </div>
  )
}

// RedeemSourceDialog removed — Amazon source toggle is now inline on /account/redemption
